import {
	ArrayPick,
	ClassNameProps,
	Column,
	compareNumbersAsc,
	compareObjects,
	getObjectEntries,
	jsxEnglishCommaJoin,
	jsxJoin,
	makeClassName,
	Row,
	StyleProps,
	toGrammaticalNumber,
	TruncatedText,
} from "@hex-insights/core";
import { Badge } from "../../../../Components/Badge";
import { Icon } from "../../../../Components/Icon";
import { CourseSection, CourseSectionNameFragment, EmployeeNameFragment } from "../../../../Utilities";
import { CourseSectionFormat, CourseSectionPeriodFormat, EmployeeFormat } from "../../../Format";
import styles from "./styles.module.css";

type SummaryViewCourseSection = CourseSectionNameFragment & {
	teacherCourseSectionEnrollments: {
		teacher: EmployeeNameFragment;
	}[];
	studentCourseSectionEnrollments: ArrayPick<CourseSection["studentCourseSectionEnrollments"], "id">;
	courseSectionPeriods: ArrayPick<
		CourseSection["courseSectionPeriods"],
		"dayOfWeek" | "startTimeMinutes" | "endTimeMinutes"
	>;
};

export type CourseSectionSummaryViewProps = {
	courseSection: SummaryViewCourseSection;
} & Partial<ClassNameProps & StyleProps>;

export function CourseSectionSummaryView({ courseSection, className, style }: CourseSectionSummaryViewProps) {
	const comparePeriods = compareObjects("startTimeMinutes", compareNumbersAsc());
	const groupedPeriods = courseSection.courseSectionPeriods.reduce((acc, period) => {
		if (!acc[period.dayOfWeek]) {
			acc[period.dayOfWeek] = [];
		}
		acc[period.dayOfWeek].push(period);
		acc[period.dayOfWeek].sort(comparePeriods);
		return acc;
	}, {} as Record<SummaryViewCourseSection["courseSectionPeriods"][0]["dayOfWeek"], SummaryViewCourseSection["courseSectionPeriods"]>);

	return (
		<Column
			justify="spaced-start"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["summary"], className)}
			style={style}
		>
			<Column justify="spaced-start" verticalSpacing="0.125rem">
				<div className={styles["summary__name-students"]}>
					<Badge
						size="small"
						color="#fff"
						backgroundColor="var(--theme-colors__teal)"
						className={styles["summary__students"]}
					>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.Users size={14} />
							{toGrammaticalNumber("Student", courseSection.studentCourseSectionEnrollments.length, true)}
						</Row>
					</Badge>

					<TruncatedText lines={2} className={styles["summary__name"]}>
						{CourseSectionFormat.name(courseSection)}
					</TruncatedText>
				</div>

				<TruncatedText className={styles["summary__teachers"]}>
					{jsxEnglishCommaJoin(
						courseSection.teacherCourseSectionEnrollments.map((e) => EmployeeFormat.name(e.teacher)),
					)}
				</TruncatedText>
			</Column>

			<Column className={styles["summary__schedule"]}>
				{getObjectEntries(groupedPeriods).map(([dayOfWeek, periods]) => (
					<Row key={dayOfWeek} justify="spaced-start" horizontalSpacing="0.375rem">
						<div className={styles["summary__schedule__weekday"]}>
							{CourseSectionPeriodFormat.Fields.dayOfWeek(dayOfWeek).slice(0, 3)}
						</div>

						<TruncatedText>
							{jsxJoin(
								periods.map(
									(period) =>
										`${CourseSectionPeriodFormat.Fields.startTimeMinutes(
											period.startTimeMinutes,
										)} - ${CourseSectionPeriodFormat.Fields.endTimeMinutes(period.endTimeMinutes)}`,
								),
								", ",
							)}
						</TruncatedText>
					</Row>
				))}
			</Column>
		</Column>
	);
}
